import { observer } from 'mobx-react';
import {
  Box,
  Button,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { vivaISVMap } from '../../constants/isv';
import useCompanyId from '../../hooks/useCompanyId';
import {
  useIntegrationsList,
  useUpdateVivaIntegrationMutation,
} from '../../stores/FetcherStores/integrationFetcher';
import { FeeTypes } from '../../model/PaymentMethod';
import { IntegrationViva } from '../../model/Integration';

interface VivaISVSelectProps {
  integrationId?: string;
}

const VivaISVSelect = observer(({ integrationId }: VivaISVSelectProps) => {
  const { t } = useTranslation();
  const companyId = useCompanyId();
  const { data: integrations } = useIntegrationsList(companyId);
  const { mutate: updateVivaIntegration } = useUpdateVivaIntegrationMutation();

  const vivaIntegration = integrations?.find(
    (integration) => integration.type === 'VIVA' && integration.id === integrationId,
  ) as IntegrationViva | undefined;
  const [terminalAmount, setTerminalAmount] = useState(vivaIntegration?.terminalFee.amount || 0.42);

  if (integrationId && !vivaIntegration) {
    return null;
  }

  const customFee = ![0.12, 0.22, 0.32, 0.42].includes(vivaIntegration?.terminalFee.amount || 0.42);

  return (
    <Card sx={{ padding: 2, background: 'primary' }}>
      <Box sx={{ marginBottom: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('vivaWallet.ISVPrice')}
        </Typography>
        <Typography>
          Debit* {Math.round((0.57 + terminalAmount) * 100) / 100}% (minimum 0.36kr)
        </Typography>
        <Typography>
          Kredit* {Math.round((0.67 + terminalAmount) * 100) / 100}% (minimum 0.36kr)
        </Typography>
        <Typography>
          Corporate* {Math.round((1.57 + terminalAmount) * 100) / 100}% (minimum 0.36kr)
        </Typography>
        <Typography>* Gäller alla EU / ESS kort, inte bara Sverige.</Typography>
        <Typography>
          Enklaorder selfservice:{' '}
          {Math.round((1 + (vivaIntegration?.selfServiceFee.amount || 0)) * 100) / 100}%
        </Typography>
        <Typography>
          Enklaorder dela nota:{' '}
          {Math.round((1 + (vivaIntegration?.splitFee.amount || 0)) * 100) / 100}%{' '}
        </Typography>
        <Typography>
          Med Enklaorder prenumeration (499kr/mån) ligger avgiften på 1.49%. Då kan även swish
          handel användas
        </Typography>
      </Box>
      {!customFee && (
        <>
          <FormControl sx={{ marginBottom: 1 }} fullWidth>
            <InputLabel id="isv-fee-label">{t('vivaWallet.revenue')}</InputLabel>
            <Select
              disabled={customFee}
              labelId="isv-fee-label"
              id="isv-fee-select"
              value={terminalAmount}
              label={t('vivaWallet.revenue')}
              onChange={(e) => {
                setTerminalAmount(Number(e.target.value));
              }}
            >
              {Object.entries(vivaISVMap).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {t(`vivaWallet.ISVRevenue.${value}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {terminalAmount !== vivaIntegration?.terminalFee.amount && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                if (integrationId) {
                  updateVivaIntegration(companyId, integrationId, {
                    terminalFee: {
                      type: FeeTypes.PERCENTAGE,
                      amount: terminalAmount,
                    },
                  });
                }
              }}
            >
              Spara
            </Button>
          )}
        </>
      )}
    </Card>
  );
});

export default VivaISVSelect;
